/**
 * V2.0.47 — Wall Handle Layer (P1 Phase 4).
 *
 * Brass dots at every shared-wall midpoint. Drag fires preview
 * blueprint updates on pointer-move and commits on pointer-up.
 * Attached doors and windows travel with the wall (Phase 3); shrunk
 * openings emit a Sonner toast (Phase 4). ADA mode adds a blue/purple
 * "ADA Boundary" toast and blocks the drag at the ADA min.
 */
import React, { useMemo, useRef, useState } from "react";
import {
  applyWallDrag,
  clampRangeForHandle,
  computeWallHandles,
  handleCentre,
} from "../lib/wallAdjacency";
import { toast } from "sonner";

export default function WallHandleLayer({
  floor,
  floorIndex,
  blueprint,
  scale,
  toX,
  toY,
  onPreviewBlueprint,
  onUpdateBlueprint,
  enabled = true,
  adaMode = false,
}) {
  const [hoverId, setHoverId] = useState(null);
  const [dragging, setDragging] = useState(null);
  const dragStateRef = useRef(null);
  const handles = useMemo(() => {
    if (!enabled || !floor) return [];
    return computeWallHandles(floor);
  }, [floor, enabled]);
  if (!enabled || handles.length === 0) return null;

  const beginDrag = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    const range = clampRangeForHandle(handle, floor);
    dragStateRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      lastDelta: 0,
      lastValidBlueprint: blueprint,
      blockedToastShown: false,
      shrinkToastShown: new Set(),
      range,
    };
    setDragging(handle.id);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch { /* ignore — pointer-capture is best-effort */ }
  };

  const moveDrag = (e) => {
    const state = dragStateRef.current;
    if (!state) return;
    const dxPx = e.clientX - state.startX;
    const dyPx = e.clientY - state.startY;
    // Pointer delta → world delta. Y axis inverted in SVG.
    let raw = state.handle.axis === "x"
      ? dxPx / scale
      : -dyPx / scale;
    raw = Math.max(state.range.min, Math.min(state.range.max, raw));
    if (Math.abs(raw - state.lastDelta) < 0.02) return;
    state.lastDelta = raw;
    if (onPreviewBlueprint) {
      const res = applyWallDrag(blueprint, floorIndex, state.handle, raw, { adaMode });
      if (res.blocked) {
        // Hold the last valid preview; show the ADA toast once.
        if (!state.blockedToastShown) {
          state.blockedToastShown = true;
          _emitBlockedToast(res.warnings, adaMode);
        }
        return;
      }
      // Show a single shrink toast per opening per drag, so the user
      // sees “Door D-3 narrowed to 30″” without it spamming on every frame.
      for (const w of res.warnings || []) {
        const key = `${w.type}:${w.id || w.kind}`;
        if (!state.shrinkToastShown.has(key)) {
          state.shrinkToastShown.add(key);
          _emitShrinkToast(w);
        }
      }
      state.lastValidBlueprint = res.blueprint;
      onPreviewBlueprint(res.blueprint);
    }
  };

  const endDrag = (e) => {
    const state = dragStateRef.current;
    if (!state) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch { /* ignore — pointer-capture is best-effort */ }
    const finalDelta = state.lastDelta;
    dragStateRef.current = null;
    setDragging(null);
    if (Math.abs(finalDelta) > 0.05) {
      const res = applyWallDrag(blueprint, floorIndex, state.handle, finalDelta, { adaMode });
      if (res.blocked) {
        // Commit failed at the ADA boundary — revert to original.
        onPreviewBlueprint?.(blueprint);
        _emitBlockedToast(res.warnings, adaMode);
        return;
      }
      onUpdateBlueprint?.(res.blueprint);
    } else if (onPreviewBlueprint) {
      onPreviewBlueprint(blueprint);
    }
  };

  return (
    <g data-testid="wall-handle-layer">
      {handles.map((h) => {
        const c = handleCentre(h);
        const cx = toX(c.x);
        const cy = toY(c.z);
        const isHover = hoverId === h.id;
        const isDrag = dragging === h.id;
        const cursor = h.axis === "x" ? "ew-resize" : "ns-resize";
        return (
          <g
            key={h.id}
            data-testid={`wall-handle-${h.id}`}
            data-axis={h.axis}
            data-exterior={h.is_exterior ? "1" : "0"}
            style={{ cursor }}
            onMouseEnter={() => setHoverId(h.id)}
            onMouseLeave={() => setHoverId((id) => (id === h.id ? null : id))}
            onPointerDown={(e) => beginDrag(e, h)}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <circle cx={cx} cy={cy} r={11} fill="transparent" />
            {(isHover || isDrag) && (
              <circle
                cx={cx} cy={cy} r={9}
                fill="rgba(196, 169, 107, 0.18)"
                stroke="rgba(196, 169, 107, 0.45)"
                strokeWidth={1}
                pointerEvents="none"
              />
            )}
            <circle
              cx={cx} cy={cy} r={(isHover || isDrag) ? 5 : 3.5}
              fill={(isHover || isDrag) ? "#c4a96b" : "rgba(196, 169, 107, 0.65)"}
              stroke="#1c1610"
              strokeWidth={1}
              pointerEvents="none"
            />
            {(isHover || isDrag) && (
              <title>
                {h.is_exterior ? "Exterior wall" : "Interior wall"} ·
                {" "}{(h.hi - h.lo).toFixed(1)}m · drag to resize
              </title>
            )}
          </g>
        );
      })}
    </g>
  );
}

// --- Toast helpers --------------------------------------------------

function _emitShrinkToast(w) {
  const inches = (w.width * 39.3701).toFixed(1);
  const label = w.id ? `${w.kind === "door" ? "Door" : "Window"} ${w.id}` : (w.kind === "door" ? "A door" : "A window");
  toast.warning(`${label} narrowed to ${inches}″ to fit the new wall length`, {
    duration: 3200,
    description: w.room_name ? `On ${w.room_name}` : undefined,
  });
}

function _emitBlockedToast(warnings, adaMode) {
  const ada = (warnings || []).find((w) => w.type === "ada_block_door" || w.type === "ada_block_window");
  const irc = (warnings || []).find((w) => w.type === "code_block_door");
  if (adaMode && ada) {
    const inches = Math.round(ada.min * 39.3701);
    const door = ada.id ? `Door ${ada.id}` : "A door";
    toast.message(`♿ ADA Boundary: Wall contraction locked. ${door} cannot shrink below ${inches}″ while Accessible Mode is active.`, {
      duration: 4500,
      style: {
        background: "linear-gradient(135deg, rgba(79, 70, 229, 0.95), rgba(124, 58, 237, 0.95))",
        color: "#fff",
        border: "1px solid rgba(196, 169, 107, 0.4)",
      },
    });
    return;
  }
  if (irc) {
    const inches = Math.round(irc.min * 39.3701);
    const door = irc.id ? `Door ${irc.id}` : "A door";
    toast.error(`Wall contraction blocked — ${door} would drop below the IRC ${inches}″ minimum`, {
      duration: 4000,
    });
    return;
  }
  toast.error("Wall contraction blocked — opening would not fit", { duration: 3500 });
}
